import { z } from 'zod';
import {
  ErpRegisterTargetFieldSchema,
  ErpSourceSystemSchema,
  ERP_IMPORT_MAX_HEADERS,
  ERP_IMPORT_MAX_HEADER_LENGTH,
} from '../erp-header-dictionary.js';

/**
 * Wire DTOs for erp_mapping_template CRUD (SIL-267, W3-T7 of
 * resources/plans/erp-register-import-spec.md sections 3.1/3.2, decision 6).
 *
 * columnMapping keys are the closed ErpRegisterTargetFieldSchema enum
 * (never a free string - spec 3.2 "target fields are a closed Zod enum,
 * never interpolated as identifiers"); values are the customer's own source
 * header, stored verbatim AS DATA. The header count/length caps mirror the
 * ones assertErpHeaderCaps enforces pre-auto-suggest (spec 3.2: <=256
 * headers, <=512 chars) - a saved template can never exceed what a single
 * register export could legitimately produce.
 */

export const erpMappingTemplateColumnMappingSchema = z
  .record(ErpRegisterTargetFieldSchema, z.string().trim().min(1).max(ERP_IMPORT_MAX_HEADER_LENGTH))
  .refine((mapping) => Object.keys(mapping).length > 0, {
    message: 'columnMapping must map at least one target field',
  })
  .refine((mapping) => Object.keys(mapping).length <= ERP_IMPORT_MAX_HEADERS, {
    message: `columnMapping cannot exceed ${ERP_IMPORT_MAX_HEADERS} mapped fields`,
  });
export type ErpMappingTemplateColumnMapping = z.infer<typeof erpMappingTemplateColumnMappingSchema>;

/** POST/PUT body - create or replace a saved per-org mapping template. */
export const erpMappingTemplateWriteSchema = z.object({
  name: z.string().trim().min(1).max(200),
  sourceSystem: ErpSourceSystemSchema.nullable().optional(),
  columnMapping: erpMappingTemplateColumnMappingSchema,
});
export type ErpMappingTemplateWrite = z.infer<typeof erpMappingTemplateWriteSchema>;

/** Stored row, as returned to the client. */
export const erpMappingTemplateRowSchema = z.object({
  id: z.string().uuid(),
  customerOrgId: z.string().uuid(),
  name: z.string(),
  sourceSystem: ErpSourceSystemSchema.nullable(),
  columnMapping: erpMappingTemplateColumnMappingSchema,
  createdByUserId: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ErpMappingTemplateRow = z.infer<typeof erpMappingTemplateRowSchema>;
